import classNames from 'classnames'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { useMutation } from 'urql'

import { isBlank, isPresent } from 'utils'

import { useCustomer } from 'hooks'
import useCustomAccount from 'hooks/useCustomerAccount'

import { customerAccessTokenAtom } from 'state/atoms'

import { CustomerAddresses, LoadingIndicator } from 'components'
import CustomerOrders from 'components/CustomerOrders'
import CustomerProfile from 'components/CustomerProfile'
import Debug from 'components/Debug'
import Divider from 'components/Divider'
import Spacer from 'components/Spacer'
import Text from 'components/Text'

import { customerAccessTokenDeleteMutation } from './index.helpers'
import {
  Button,
  CustomerProfileWrapper,
  Main,
  Navigation,
  NavigationItem,
  OrdersAndAddresses,
  Root,
} from './index.style'
import CaretSvg from './svg/caret.svg'

export default function Index() {
  const router = useRouter()

  const customer = useCustomer()
  const customerAccount = useCustomAccount(customer.data)

  const [view, setView] = useState('orders')

  const [customerAccessToken, setCustomerAccessToken] = useRecoilState(customerAccessTokenAtom)
  const [_customerAccessTokenDeleteResult, customerAccessTokenDelete] = useMutation(customerAccessTokenDeleteMutation)

  const [loggingOut, setLoggingOut] = useState(false)

  const logout = async () => {
    setLoggingOut(true)
    const result = await customerAccessTokenDelete({ customerAccessToken })
    const {
      data: {
        customerAccessTokenDelete: { userErrors },
      },
    } = result

    if (isPresent(userErrors)) return setLoggingOut(false)

    setCustomerAccessToken(null)
    router.replace('/account/login')
  }

  useEffect(() => {
    if (customer.data || customer.loading) return undefined

    setCustomerAccessToken(null)
    router.replace('/account/login')
  }, [router, customer, setCustomerAccessToken])

  if (isBlank(customer)) return null

  return (
    <>
      <Head>
        <title>Account - The Armoury</title>
      </Head>
      <Root>
        {customerAccount.loading || loggingOut ? (
          <div className="grid place-content-center p-8 min-h-[10rem]">
            <LoadingIndicator />
          </div>
        ) : (
          <Main>
            <CustomerProfileWrapper>
              <CustomerProfile customer={customerAccount.data}>
                <Spacer y={8} />
                <Button onClick={logout}>
                  <Text font="proxima" tracking="wide" transform="uppercase" size="sm">
                    Log out
                  </Text>
                </Button>
              </CustomerProfile>
            </CustomerProfileWrapper>
            <OrdersAndAddresses>
              <Navigation>
                <NavigationItem className={classNames({ active: view === 'orders' })} onClick={() => setView('orders')}>
                  <Text font="proxima" size="base" weight="semibold">
                    Orders
                  </Text>
                  <CaretSvg />
                </NavigationItem>
                <NavigationItem
                  className={classNames({ active: view === 'addresses' })}
                  onClick={() => setView('addresses')}
                >
                  <Text font="proxima" size="base" weight="semibold">
                    Addresses
                  </Text>
                  <CaretSvg />
                </NavigationItem>
              </Navigation>
              {view === 'orders' && <CustomerOrders orders={customerAccount?.data?.events} />}
              {view === 'addresses' && <CustomerAddresses addresses={customerAccount.data?.addresses} />}
            </OrdersAndAddresses>
          </Main>
        )}
      </Root>
      <Divider />
    </>
  )
}
